import PhoenixDocCard from 'components/base/PhoenixDocCard';
import React, { useEffect, useState } from 'react';
import { RoleService, Role } from 'services/role.service';
import AuthService from 'services/auth.service';
import { getAccount, OneAccount } from 'services/account.service';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormResultMessage from './FormResultMessage';

const UpdateFullUserForm: React.FC<{
  accountId: number;
  onClose: () => void;
  onUpdate?: () => void;
}> = ({ accountId, onClose, onUpdate }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleNames, setSelectedRoleNames] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<
    'successupdate' | 'errorupdate'
  >('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<OneAccount | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      const rolesData = await RoleService.getAllRoles();
      setRoles(rolesData);
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getAccount(accountId);
        setCurrentUser(userData);
        setSelectedRoleNames(userData.roles);
        formik.setValues({
          name: userData.name,
          lastname: userData.lastname,
          username: userData.username,
          email: userData.email,
          activation_code: userData.activation_code,
          roles: userData.roles
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [accountId]);

  const formik = useFormik({
    initialValues: {
      name: '',
      lastname: '',
      username: '',
      email: '',
      activation_code: false,
      roles: ''
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(3, 'First name must be at least 3 characters')
        .required('First name is required'),
      lastname: Yup.string()
        .min(3, 'Last name must be at least 3 characters')
        .required('Last name is required'),
      username: Yup.string()
        .min(10, 'Username must be at least 10 characters')
        .max(50, 'Username must be less than 50 characters')
        .test(
          'no-at-sign',
          '"@" is not allowed in username',
          value => !value || !/@/.test(value)
        )
        .required('Username is required'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required')
    }),
    onSubmit: async values => {
      try {
        // eslint-disable-next-line
        const rolesArray = selectedRoleNames.split(',').map(role => role.trim());
        await AuthService.updateoneaccount(
          accountId,
          values.username,
          values.name,
          values.lastname,
          values.email,
          values.activation_code ? 1 : 0,
          rolesArray
        );
        setMessage('The user information has been updated');
        setMessageType('successupdate');
        if (onUpdate) {
          onUpdate();
        }
      } catch (error) {
        setMessage('I apologize, but it seems there was a hiccup! ' + error);
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    }
  });

  useEffect(() => {
    if (currentUser) {
      setSelectedRoleNames(currentUser.roles);
      formik.setValues({
        name: currentUser.name,
        lastname: currentUser.lastname,
        username: currentUser.username,
        email: currentUser.email,
        activation_code: currentUser.activation_code,
        roles: currentUser.roles
      });
    }
  }, [currentUser]);

  const handleCheckboxChange = (role: Role) => {
    const trimmedRoleName = role.rolename.trim();
    setSelectedRoleNames(prevSelectedRoleNames => {
      const rolesArray = prevSelectedRoleNames.split(',').map(r => r.trim());
      if (rolesArray.includes(trimmedRoleName)) {
        return rolesArray.filter(name => name !== trimmedRoleName).join(',');
      } else {
        return [...rolesArray, trimmedRoleName].join(',');
      }
    });
  };

  useEffect(() => {
    formik.setFieldValue('roles', selectedRoleNames);
  }, [selectedRoleNames]);

  const closeModal = () => {
    setModalVisible(false);
    onClose();
  };

  return (
    <div>
      <form className="mb-1" onSubmit={formik.handleSubmit}>
        <div className="p-6 pt-4 pb-0">
          <label className="form-label">First name</label>
          <input
            type="text"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
            title="First name"
            aria-label="First name"
          />
          {formik.touched.name && formik.errors.name ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.name}
            </div>
          ) : null}
        </div>
        <div className="p-6 pt-0 pb-0">
          <label className="form-label">Last name</label>
          <input
            type="text"
            name="lastname"
            value={formik.values.lastname}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
            title="Last name"
            aria-label="Last name"
          />
          {formik.touched.lastname && formik.errors.lastname ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.lastname}
            </div>
          ) : null}
        </div>
        <div className="p-6 pt-0 pb-0">
          <label className="form-label">Username</label>
          <input
            type="text"
            name="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
            title="Username"
            aria-label="Username"
          />
          {formik.touched.username && formik.errors.username ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.username}
            </div>
          ) : null}
        </div>
        <div className="p-6 pt-0 pb-0">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
            title="Email address"
            aria-label="Email address"
          />
          {formik.touched.email && formik.errors.email ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.email}
            </div>
          ) : null}
        </div>
        <label className="form-label p-8 pt-3 pb-0">
          Two Factor Authentication (TFA)
        </label>
        <div className="p-6 pt-0 pb-0">
          <PhoenixDocCard className={'border'}>
            <PhoenixDocCard.Body className="p-0 pt-0 pb-0">
              <div className="form-check form-switch p-0 mb-0">
                <label className="form-check-label p-4 pt-0 pb-0 mb-0">
                  <input
                    type="checkbox"
                    name="activation_code"
                    checked={formik.values.activation_code}
                    onChange={formik.handleChange}
                    className="form-check-input"
                    title="Enable Two Factor Authentication"
                    aria-label="Enable Two Factor Authentication"
                  />
                  Two Factor Authentication is required for this account
                </label>
              </div>
            </PhoenixDocCard.Body>
          </PhoenixDocCard>
        </div>
        <label className="form-label p-8 pt-3 pb-0">
          Select account authorization level
        </label>
        <div className="p-6 pt-0 pb-0">
          <PhoenixDocCard className={'border'}>
            <PhoenixDocCard.Body className="p-0 pt-0 pb-0">
              {roles.map(role => (
                <div key={role.id} className="form-check form-switch p-0 mb-0">
                  <label className="form-check-label p-4 pt-0 pb-0 mb-0">
                    <input
                      type="checkbox"
                      value={role.rolename}
                      checked={selectedRoleNames.includes(role.rolename)}
                      onChange={() => handleCheckboxChange(role)}
                      disabled={role.id === 3} // Disable if role id is 3
                      className="form-check-input"
                      title={role.description}
                      aria-label={role.description}
                    />
                    {role.description}
                  </label>
                </div>
              ))}
            </PhoenixDocCard.Body>
          </PhoenixDocCard>
        </div>
        <hr className="p-0 pt-3 mb-0" />
        <div className="p-3 pt-0 text-end">
          <button
            type="button"
            className="btn btn-secondary btn-block p-2 px-4 m-1 mt-0"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-block p-2 px-4 m-1 mt-0"
          >
            Update Account
          </button>
        </div>
      </form>
      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </div>
  );
};

export default UpdateFullUserForm;
